import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import FormContainer from "../shared/FormContainer";
import CustomInput from "../shared/CustomInput";
import Button from "../shared/Button";
import { Api } from "../../network/api";
import { saveToken } from "../../utils/cookies";
import {
  registerStart,
  registerSuccess,
  registerFailure,
  clearError,
} from "../../redux/slice/userSlice";

const RegisterForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.user);

  const [formData, setFormData] = useState({
    name: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});

  // Clear errors when component mounts
  useEffect(() => {
    dispatch(clearError());
    setErrors({});
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name) {
      newErrors.name = "Name is required";
    } else if (formData.name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    if (!formData.username) {
      newErrors.username = "Username is required";
    } else if (formData.username.length < 2) {
      newErrors.username = "Username must be at least 2 characters";
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)
    ) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
      };
      const response = await Api.register(payload);
      if (response?.status === 200) {
        navigate("/login");
      } else {
        dispatch(registerFailure("Registration failed. Please try again."));
        setErrors({ general: "Registration failed. Please try again." });
      }
    } catch (error) {
      console.error("Registration error:", error);
      const errorMessage = error?.response?.data?.detail || 'Registration failed. Please try again.';
      dispatch(registerFailure(errorMessage));
      setErrors({ general: errorMessage });
    }
  };

  const handleLoginClick = () => {
    dispatch(clearError());
    setErrors({});
    navigate("/login");
  };

  return (
    <FormContainer title="Create Account" subtitle="Join us today">
      <form
        onSubmit={handleSubmit}
        onInvalid={validateForm}
        className="space-y-6"
      >
        {(errors.general || error) && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-left animate-shake">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {errors.general || error}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 animate-fadeInUp animation-delay-100"></div>
        <div className="animate-fadeInUp animation-delay-50">
          <CustomInput
            type="text"
            name="name"
            label="Name"
            placeholder="Enter your name"
            value={formData.name}
            onChange={handleInputChange}
            error={errors.name}
            required
            autoComplete="off"
          />
        </div>{" "}
        <div className="animate-fadeInUp animation-delay-100">
          {/* <CustomInput
            type="text"
            name="username"
            label="Username"
            placeholder="Enter your username"
            value={formData.username}
            onChange={handleInputChange}
            error={errors.username}
            required
            autoComplete="off"
          /> */}
        </div>{" "}
        <div className="animate-fadeInUp animation-delay-200">
          <CustomInput
            type="email"
            name="email"
            label="Email Address"
            placeholder="Enter your email"
            value={formData.email}
            onChange={handleInputChange}
            error={errors.email}
            required
            autoComplete="off"
          />
        </div>
        <div className="animate-fadeInUp animation-delay-300">
          <CustomInput
            type="password"
            name="password"
            label="Password"
            placeholder="Create a password"
            value={formData.password}
            onChange={handleInputChange}
            error={errors.password}
            required
            autoComplete="new-password"
          />
        </div>
        <div className="animate-fadeInUp animation-delay-400">
          <CustomInput
            type="password"
            name="confirmPassword"
            label="Confirm Password"
            placeholder="Confirm your password"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            error={errors.confirmPassword}
            required
            autoComplete="new-password"
          />
        </div>
        <div className="flex items-center animate-fadeInUp animation-delay-500">
          <input
            id="terms"
            name="terms"
            type="checkbox"
            required
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all duration-200 hover:scale-110"
          />
          <label
            htmlFor="terms"
            className="ml-2 block text-sm text-gray-900 hover:text-gray-700 transition-colors duration-200"
          >
            I agree to the{" "}
            <a
              href="/terms"
              className="text-blue-600 hover:text-blue-500 transition-colors duration-200 hover:underline"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="/privacy"
              className="text-blue-600 hover:text-blue-500 transition-colors duration-200 hover:underline"
            >
              Privacy Policy
            </a>
          </label>
        </div>
        <div className="animate-fadeInUp animation-delay-600">
          <Button
            type="submit"
            loading={loading}
            disabled={loading}
            className="w-full hover-lift"
            size="large"
          >
            Create Account
          </Button>
        </div>
        <div className="text-center animate-fadeInUp animation-delay-700">
          <p className="text-sm text-gray-600">
            Already have an account?{" "}
            <button
              type="button"
              onClick={handleLoginClick}
              className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200 hover:underline"
            >
              Sign in
            </button>
          </p>
        </div>
      </form>
    </FormContainer>
  );
};

export default RegisterForm;
